import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
	MdEdit,
	MdEmail,
	MdPhone,
	MdPerson,
	MdCalendarMonth,
	MdBusiness,
	MdMedicalServices,
	MdGroups,
	MdLockReset,
} from "react-icons/md";
import { useAuth } from "../../Context/AuthContext";
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { getUserById, getAllUsers } from "../../Services/UserService";
import { APPRoutes } from "../../Utils/Route";
import { formatUtcToLocalDateTime } from "../../Utils/DateTime";
import { toast } from "react-toastify";
import { forgotPassword } from "../../Services/AuthenticationService";
import ViewEmptyState from "../../Components/Common/ViewEmptyState";
import BackButton from '../../Components/Common/BackButton';

const STATUS_STYLE = {
	Active: { backgroundColor: "var(--hc-status-success-bg)", color: "var(--hc-status-success-text)" },
	Inactive: { backgroundColor: "var(--hc-status-inactive-bg)", color: "var(--hc-status-inactive-text)" },
};


export default function UsersView() {
	const navigate = useNavigate();
	const { id } = useParams();
	const location = useLocation();
	const { token, permissions, decodedToken } = useAuth();
	const userPermissionCodes = React.useMemo(
		() => resolveUserPermissionCodes({ permissions, decodedToken }),
		[permissions, decodedToken],
	);
	const canEditUser = permissionHas(userPermissionCodes, PermissionCodes.USERS_EDIT, decodedToken);
	const canViewList = permissionHas(userPermissionCodes, PermissionCodes.USERS_LIST, decodedToken);

	const [user, setUser] = React.useState(location.state?.user || null);
	const resolvedId = React.useMemo(() => id || location.state?.user?.Id || decodedToken?.user_id || null, [id, location.state, decodedToken]);
	const [loading, setLoading] = React.useState(!location.state?.user);
	const [resetPasswordLoading, setResetPasswordLoading] = React.useState(false);



	const loadData = async (userId) => {
		try {
			setLoading(true);
			let targetId = userId || id;
			if (!targetId || targetId === 'first' || targetId === ':id') {
				const listRes = await getAllUsers(token);
				const rawList = listRes?.data?.Users || listRes?.data?.users || listRes?.data || listRes;
				const list = Array.isArray(rawList) ? rawList : [];
				targetId = list[0]?.Id || list[0]?.id || list[0]?.UserId || null;
			}
			if (!targetId) {
				setUser(null);
				return;
			}
			const userRes = await getUserById(targetId, token);
			if (userRes?.is_success) {
				setUser(userRes.data);
			} else if (userRes?.is_success === false && userRes?.status_code === 500) {
				toast.error('Internal Server Error. Please Contact Support Team.');
				setUser(null);
			} else {
				toast.error(userRes?.message || "Failed to load user");
				setUser(null);
			}
		} catch (err) {
			console.error(err);
			setUser(null);
		} finally {
			setLoading(false);
		}
	};


	const SendResetPassword = async () => {
		if (!user?.EmailAddress) {
			toast.error("This user does not have an email address.");
			return;
		}

		try {
			setResetPasswordLoading(true);
			const response = await forgotPassword(user.EmailAddress);
			if (response?.is_success) {
				toast.success(response.message || "Password reset link sent successfully.");
			} else {
				toast.error(response?.message || "Failed to send password reset link.");
			}
		} catch (error) {
			console.error("Error sending password reset link:", error);
			toast.error(error?.message || "Failed to send password reset link. Please try again.");
		} finally {
			setResetPasswordLoading(false);
		}
	}


	React.useEffect(() => {
		if (!token) return;
		if (!location.state?.user) {
			loadData(resolvedId);
		}
	}, [token, resolvedId, location.state]);

	const statusName = user?.StatusName || "Unknown";
	const createdOnLocal = React.useMemo(
		() => formatUtcToLocalDateTime(user?.CreatedOn),
		[user?.CreatedOn]
	);
	const modifiedOnLocal = React.useMemo(
		() => formatUtcToLocalDateTime(user?.ModifiedOn),
		[user?.ModifiedOn]
	);
	const USER_TYPE_LABELS = { 78: "Billing Member", 79: "Credentialing Member", 80: "Staff", 81: "Provider", 82: "General" };
	const rawUserType = user?.UserType ?? user?.user_type;
	const userType = rawUserType !== null && rawUserType !== undefined ? (USER_TYPE_LABELS[Number(rawUserType)] || "General") : null;
	const provider = user?.Provider || {};
	const providerName = [provider?.FirstName, provider?.MiddleName, provider?.LastName, provider?.SuffixName]
		.filter(Boolean)
		.join(" ")
		.trim();
	const providerTitle = provider?.ProviderTitleName || "";
	const providerEmail = provider?.WorkEmail || provider?.PersonalEmail || "-";
	const providerPhone = provider?.MobilePhone || provider?.HomePhone || "-";
	const providerOrgId = provider?.OrganizationId || "-";
	const staff = user?.Staff || {};
	const staffName = [staff?.FirstName, staff?.LastName].filter(Boolean).join(" ").trim();
	const staffTitle = staff?.Title || "";
	const staffEmail = staff?.Email || "-";
	const staffPhone = staff?.PhoneNumber || "-";
	const organizations = Array.isArray(user?.Organizations) ? user.Organizations : [];


	if (loading) {
		return (
			<div className="container-fluid py-4">
				<div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
					<div className="skeleton position-relative overflow-hidden rounded-pill" style={{ height: 40, width: 110 }} />
					<div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 120, borderRadius: 10 }} />
				</div>
				<div className="p-3 p-lg-4 mb-4 hc-surface overflow-hidden">
					<div className="row g-4 g-xl-5 align-items-stretch">
						<div className="col-xl-9">
							<div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-4 h-100">
								<div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 124, height: 124, borderRadius: 28 }} />
								<div className="flex-grow-1 w-100">
									<div className="d-flex gap-2 mb-3">
										<div className="skeleton position-relative overflow-hidden rounded-3" style={{ height: 28, width: 86 }} />
										<div className="skeleton position-relative overflow-hidden rounded-3" style={{ height: 28, width: 64 }} />
									</div>
									<div className="skeleton mb-2" style={{ height: 38, width: "72%", borderRadius: 10 }} />
									<div className="skeleton mb-1 rounded-3" style={{ height: 16, width: "86%" }} />
									<div className="skeleton mb-3 rounded-3" style={{ height: 16, width: "58%" }} />
									<div className="skeleton mb-2 rounded-3" style={{ height: 20, width: 110 }} />
									<div className="row g-3">
										<div className="col-12 col-md-6">
											<div className="skeleton position-relative overflow-hidden rounded-4 w-100" style={{ height: 86 }} />
										</div>
										<div className="col-12 col-md-6">
											<div className="skeleton position-relative overflow-hidden rounded-4 w-100" style={{ height: 86 }} />
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="col-xl-3">
							<div className="skeleton h-100 rounded-4" style={{ minHeight: 220 }} />
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (!user) {
		return (
			<ViewEmptyState
				title="No Users Available"
				description="There are no user records available to display."
				icon={MdPerson}
				canViewList={canViewList}
				backRoute={APPRoutes.USERS}
				backLabel="Back to Users"
			/>
		);
	}

	return (
		<div className="container-fluid py-4">
			<div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
				{canViewList && (
					<BackButton onClick={() => navigate(APPRoutes.USERS)} />
				)}
				   

				{canEditUser ? (
					<div className="d-flex gap-2">
					<button
					type="button"
					className="btn hc-bg-primary text-white d-inline-flex align-items-center gap-2 "
					onClick={SendResetPassword}
					disabled={resetPasswordLoading}
					>
					{resetPasswordLoading ? (
						<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
					) : (
						<MdLockReset size={18} />
					)}
					<span>{resetPasswordLoading ? "Send Reset Password..." : "Send Reset Password"}</span>
				</button>
					<button
						type="button"
						className="btn hc-bg-primary text-white d-inline-flex align-items-center gap-3"
						onClick={() => navigate(APPRoutes.USERS_EDIT.replace(":id", user.Id))}
					>
						<MdEdit size={18} /> Edit User
					</button>
					</div>
				) : null}

				     
			</div>

			<div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
				<div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
				<div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
				<div className="row g-4 align-items-center position-relative">
					<div className="col-lg-8">
						<div className="d-flex flex-wrap align-items-center gap-3 mb-3">
							<span
								className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold"
								style={{ fontSize: "0.82rem", backgroundColor: STATUS_STYLE[statusName]?.backgroundColor || "var(--hc-status-neutral-bg)", color: STATUS_STYLE[statusName]?.color || "var(--hc-table-footer-text)" }}
							>
								{statusName}
							</span>
							{user.Role?.Name ? (
								<span
									className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold"
									style={{ fontSize: "0.82rem", backgroundColor: "rgba(var(--hc-white-rgb), 0.12)", color: "var(--hc-white)" }}
								>
									{user.Role.Name}
								</span>
							) : null}
							{userType && userType !== "General" && (
								<span
									className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold"
									style={{ fontSize: "0.82rem", backgroundColor: "var(--hc-teal-bg-alt)", color: "var(--hc-teal-text-alt)" }}
								>
									{userType}
								</span>
							)}
						</div>
						<h2 className="fw-bold text-white mb-2" style={{ fontSize: "clamp(1.45rem, 2.2vw, 2rem)" }}>
							{`${user.FirstName || ""} ${user.LastName || ""}`.trim()}
						</h2>
						<p className="mb-1" style={{ color: "rgba(var(--hc-white-rgb), 0.78)", fontSize: "0.95rem" }}>
							<MdEmail size={14} className="me-1" />{user.EmailAddress || "-"}
						</p>
						<p className="mb-1" style={{ color: "rgba(var(--hc-white-rgb), 0.65)", fontSize: "0.88rem" }}>
							<MdPhone size={14} className="me-1" />{user.Phone || "-"}
						</p>
						<p className="mb-0" style={{ color: "rgba(var(--hc-white-rgb), 0.65)", fontSize: "0.88rem" }}>
							<MdBusiness size={14} className="me-1" />{user.ClientName || "Global User"}
						</p>
					</div>
					<div className="col-lg-4">
						<div className="p-3 rounded-4" style={{ backgroundColor: "rgba(var(--hc-white-rgb), 0.10)" }}>
							<p className="mb-1 small" style={{ color: "rgba(var(--hc-white-rgb), 0.65)" }}>
								User Type
							</p>
							<p className="fw-bold text-white mb-3" style={{ fontSize: "1.55rem", lineHeight: 1.1 }}>
								{userType || "Not Assigned"}
							</p>
							<p className="mb-1 small" style={{ color: "rgba(var(--hc-white-rgb), 0.65)" }}>
								User ID
							</p>
							<p className="fw-semibold text-white mb-0 text-break" style={{ fontSize: "0.85rem" }}>
								#{user.Id || "-"}
							</p>
						</div>
					</div>
				</div>
			</div>

			{providerName && (
				<div className="row g-4 mb-4">
					<div className="col-12">
						<div className="p-4 p-lg-5 hc-surface hc-card-primary">
							<h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
								<MdMedicalServices size={22} className="hc-text-primary" /> Provider Details
							</h5>
							<div className="row g-3">
								<div className="col-12 col-md-6 col-xl-3 col-lg-6">
									<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
										<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
											<MdPerson size={18} className="hc-text-primary" />
										</span>
										<div className="text-start">
											<div className="small text-muted">Provider Name</div>
											<div className="fw-medium text-dark">{providerName || "-"}</div>
										</div>
									</div>
								</div>
								<div className="col-12 col-md-6 col-xl-3 col-lg-6">
									<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
										<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
											<MdBusiness size={18} className="hc-text-primary" />
										</span>
										<div className="text-start">
											<div className="small text-muted">Provider Title</div>
											<div className="fw-medium text-dark">{providerTitle || "-"}</div>
										</div>
									</div>
								</div>
								<div className="col-12 col-md-6 col-xl-3 col-lg-6">
									<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
										<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
											<MdEmail size={18} className="hc-text-primary" />
										</span>
										<div className="text-start">
											<div className="small text-muted">Email</div>
											<div className="fw-medium text-dark">{providerEmail}</div>
										</div>
									</div>
								</div>
								<div className="col-12 col-md-6 col-xl-3 col-lg-6">
									<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
										<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
											<MdPhone size={18} className="hc-text-primary" />
										</span>
										<div className="text-start">
											<div className="small text-muted">Phone</div>
											<div className="fw-medium text-dark">{providerPhone}</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{staffName && (
				<div className="row g-4 mb-4">
					<div className="col-12">
						<div className="p-4 p-lg-5 hc-surface hc-card-primary">
							<h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
								<MdGroups size={22} className="hc-text-primary" /> Staff Details
							</h5>
							<div className="row g-3">
								<div className="col-12 col-md-6 col-xl-3 col-lg-6">
									<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
										<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
											<MdPerson size={18} className="hc-text-primary" />
										</span>
										<div className="text-start">
											<div className="small text-muted">Staff Name</div>
											<div className="fw-medium text-dark">{staffName || "-"}</div>
										</div>
									</div>
								</div>
								<div className="col-12 col-md-6 col-xl-3 col-lg-6">
									<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
										<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
											<MdBusiness size={18} className="hc-text-primary" />
										</span>
										<div className="text-start">
											<div className="small text-muted">Title</div>
											<div className="fw-medium text-dark">{staffTitle || "-"}</div>
										</div>
									</div>
								</div>
								<div className="col-12 col-md-6 col-xl-3 col-lg-6">
									<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
										<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
											<MdEmail size={18} className="hc-text-primary" />
										</span>
										<div className="text-start">
											<div className="small text-muted">Email</div>
											<div className="fw-medium text-dark">{staffEmail}</div>
										</div>
									</div>
								</div>
								<div className="col-12 col-md-6 col-xl-3 col-lg-6">
									<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
										<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
											<MdPhone size={18} className="hc-text-primary" />
										</span>
										<div className="text-start">
											<div className="small text-muted">Phone</div>
											<div className="fw-medium text-dark">{staffPhone}</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{organizations.length > 0 && (
				<div className="row g-4 mb-4">
					<div className="col-12">
						<div className="p-4 p-lg-5 hc-surface hc-card-primary">
							<h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
								<MdGroups size={22} className="hc-text-primary" /> Organizations
							</h5>
							<div className="row g-3">
								{organizations.map((org, idx) => (
									<div key={idx} className="col-12 col-md-6 col-lg-4">
										<div className="h-100 rounded-4 border overflow-hidden shadow-sm" style={{ backgroundColor: "var(--hc-gray-bg-soft)" }}>
											<div className="p-3 d-flex align-items-start gap-3">
												<div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0" style={{ width: 48, height: 48, backgroundColor: "var(--hc-icon-blue-bg)" }}>
													<MdBusiness size={24} className="hc-text-primary" />
												</div>
												<div className="flex-grow-1 min-w-0">
													<div className="small text-muted mb-1" style={{ fontSize: "0.75rem" }}>Organization</div>
													<div className="fw-semibold text-dark mb-3" style={{ fontSize: "0.95rem", wordBreak: "break-word" }}>
														{org.Name || "-"}
													</div>
													<div className="row g-2">
														<div className="col-6">
															<div className="small text-muted" style={{ fontSize: "0.7rem" }}>CID</div>
															<div className="fw-medium text-dark" style={{ fontSize: "0.85rem" }}>{org.CID || "-"}</div>
														</div>
														<div className="col-6">
															<div className="small text-muted" style={{ fontSize: "0.7rem" }}>DBA</div>
															<div className="fw-medium text-dark" style={{ fontSize: "0.85rem" }}>{org.DBA || "-"}</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			)}

			<div className="row g-4 mb-4">
				<div className="col-12">
					<div className="p-4 p-lg-5 hc-surface hc-card-primary">
						<h5 className="fw-bold mb-3 d-flex align-items-center gap-2 hc-text-primary">
							<MdPerson size={22} className="hc-text-primary" /> Audit Info
						</h5>
						<div className="row g-3">
							<div className="col-12 col-md-6">
								<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
									<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
										<MdPerson size={18} className="hc-text-primary" />
									</span>
									<div className="text-start">
										<div className="small text-muted">Created By</div>
										<div className="fw-medium text-dark">{user.CreatedBy || "-"}</div>
									</div>
								</div>
							</div>
							<div className="col-12 col-md-6">
								<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
									<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
										<MdCalendarMonth size={18} className="hc-text-primary" />
									</span>
									<div className="text-start">
										<div className="small text-muted">Created On</div>
										<div className="fw-medium text-dark">{createdOnLocal}</div>
									</div>
								</div>
							</div>
							<div className="col-12 col-md-6">
								<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
									<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
										<MdPerson size={18} className="hc-text-primary" />
									</span>
									<div className="text-start">
										<div className="small text-muted">Modified By</div>
										<div className="fw-medium text-dark">{user.ModifiedBy || "-"}</div>
									</div>
								</div>
							</div>
							<div className="col-12 col-md-6">
								<div className="h-100 d-flex align-items-center gap-3 px-3 py-3 rounded-4 bg-light border">
									<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border" style={{ width: 40, height: 40 }}>
										<MdCalendarMonth size={18} className="hc-text-primary" />
									</span>
									<div className="text-start">
										<div className="small text-muted">Modified On</div>
										<div className="fw-medium text-dark">{modifiedOnLocal}</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

